<script setup lang="ts">
import partnerApi from '@/services/partners.service'
import type { ConfigType, PartnerSelector } from '@/types/models/PartnerConfigModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { AutocompleteField, StackLayout, TypedInput } from '@rfc/rfc-components-library'
import { localFallback } from '@rfc/rfc-components-library/locales'
import { type ExtendedPrimitives } from '@rfc/rfc-components-library/types'
import { EDebounceTime } from '@rfc/rfc-components-library/utils'
import { useDebounceFn } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()

type Props = {
    partner_config?: Record<string, ExtendedPrimitives> | null
    partnerId?: number
    currentPartner?: PartnerModel
}
const props = defineProps<Props>()
type Emits = {
    (e: 'update', value: PartnerSelector): void
}
const emit = defineEmits<Emits>()

const loadingEvents = ref(false)

const getPartners = (name?: string) => {
    loadingEvents.value = true
    partnerApi
        .getPartners(name ? { filters: { name } } : undefined, true)
        .then(res => (partnersOptions.value = res.data ?? []))
        .finally(() => (loadingEvents.value = false))
}
onMounted(() => getPartners())

const selectedPartner = ref<PartnerModel>()

const partnersOptions = ref<PartnerModel[]>([])
const computedPartnersOptions = computed(() =>
    props.currentPartner
        ? partnersOptions.value?.find(item => item.id === props.currentPartner?.id)
            ? partnersOptions.value
            : [props.currentPartner, ...(partnersOptions.value ?? [])]
        : partnersOptions.value
)

const handleSelect = (partner: PartnerModel) => {
    if (!partner || !partner?.id) return
    selectedPartner.value = partner
    if (props.partnerId === partner?.id) return emit('update', { partner_id: partner?.id })
    emit('update', { partner_id: partner?.id, partner_config: undefined })
}

const handleChange = (value: string) => {
    if (value === '' || !!value) return
    isLoadingEvents.value = true
    debouncedQuestion(value)
}
const isLoadingEvents = ref(false)
const debouncedQuestion = useDebounceFn(
    (value: string) => getPartners(value),
    EDebounceTime.DEFAULT
)
</script>

<template>
    <StackLayout direction="column" :gap="12">
        <AutocompleteField
            :label="$t('event.model.partnerName')"
            :options="computedPartnersOptions"
            :modelValue="computedPartnersOptions.find(item => item?.id === partnerId)?.name"
            labelKey="name"
            valueKey="id"
            @typing="handleChange"
            @select="handleSelect($event as PartnerModel)"
            clearable
            class="full-width" />
        <StackLayout
            direction="column"
            :gap="12"
            v-if="selectedPartner?.config && !Array.isArray(selectedPartner?.config)">
            <TypedInput
                v-for="([itemKey, itemValue], key) in Object.entries(selectedPartner.config.event)"
                :key="key"
                :modelValue="partner_config?.[itemKey]"
                :label="
                    (itemValue as ConfigType)?.label
                        ? (localFallback((itemValue as ConfigType)?.label, i18n) ?? itemKey)
                        : itemKey
                "
                :dataType="(itemValue as ConfigType)?.type ?? (itemValue as string)"
                @change="
                    $emit('update', {
                        partner_config: { ...(partner_config ?? {}), [itemKey]: $event }
                    })
                "
                class="w-full" />
        </StackLayout>
    </StackLayout>
</template>
